import React, { useState } from "react"
import utilStyles from "../../asset/styles/util/util.module.scss"
import Grid from "@mui/material/Grid"
import Table, { TableColumn } from "component/table/table"
import InputText from "component/inputText/inputText"
import Checkbox from "component/checkbox/checkbox"
import IconsColor from "util/iconsColor"
import {
    ExcursionDetailModel,
    ExcursionDetailAddOnModel,
    ExcursionDetailAddOnPricingModel,
} from "../../models"

const ADULT_CATEGORY = "Adult"

const capitalize = (s: string) =>
    s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s

/** Get adult category_id from excursion (excursion_pricing or child_policy). Returns 0 if not found. */
function getAdultCategoryId(excursion: ExcursionDetailModel): number {
    const fromPricing = (excursion.excursion_pricing ?? []).find(
        (p) => (p.category_name ?? "").trim().toLowerCase() === "adult"
    )
    if (fromPricing?.category_id != null && fromPricing.category_id !== 0) return fromPricing.category_id
    const fromPolicy = (excursion.child_policy ?? []).find(
        (c) => (c.category_name ?? "").trim().toLowerCase() === "adult"
    )
    return fromPolicy?.category_id ?? 0
}

/** Categories from child_policy that are not free of charge (paid categories for Add Ons) */
function getPaidNonAdultCategories(
    childPolicy: ExcursionDetailModel["child_policy"]
): { category_name: string; category_id: number }[] {
    return (childPolicy ?? [])
        .filter(
            (c) =>
                !c.flag_free_of_charge &&
                (c.category_name ?? "").trim().toLowerCase() !== "adult"
        )
        .map((c) => ({
            category_name: (c.category_name ?? "").trim(),
            category_id: c.category_id ?? 0,
        }))
}

function getPriceByCategory(
    addOn: ExcursionDetailAddOnModel,
    category: string
): number {
    const item = (addOn.add_on_pricing || []).find(
        (p) => (p.category_name ?? "").toLowerCase() === (category ?? "").toLowerCase()
    )
    return item?.price ?? 0
}

function ensurePricingForCategories(
    addOn: ExcursionDetailAddOnModel,
    paidCategories: { category_name: string; category_id: number }[],
    adultCategoryId: number
): ExcursionDetailAddOnPricingModel[] {
    const pricing = addOn.add_on_pricing || []
    const result = [...pricing]
    const hasAdult = pricing.some((p) => (p.category_name ?? "").toLowerCase() === "adult")
    if (!hasAdult) {
        result.push({
            add_on_pricing_id: "",
            excursion_add_on_id: addOn.excursion_add_on_id,
            category_id: adultCategoryId,
            category_name: ADULT_CATEGORY,
            price: 0,
        })
    }
    for (const cat of paidCategories) {
        const key = (cat.category_name ?? "").toLowerCase()
        const hasCat = result.some((p) => (p.category_name ?? "").toLowerCase() === key)
        if (!hasCat) {
            result.push({
                add_on_pricing_id: "",
                excursion_add_on_id: addOn.excursion_add_on_id,
                category_id: cat.category_id,
                category_name: cat.category_name,
                price: 0,
            })
        }
    }
    return result
}

interface ExcursionAddOnsProps {
    excursion: ExcursionDetailModel
    onChange: (updates: Partial<ExcursionDetailModel>) => void
    readOnly?: boolean
    error?: string
}

const ExcursionAddOns: React.FC<ExcursionAddOnsProps> = ({
    excursion,
    onChange,
    readOnly = false,
    error,
}) => {
    const data = excursion.excursion_add_ons || []
    const childPolicy = excursion.child_policy || []
    const paidCategories = getPaidNonAdultCategories(childPolicy)
    const adultCategoryId = getAdultCategoryId(excursion)
    const [duplicateNameError, setDuplicateNameError] = useState<string | null>(null)

    const checkDuplicateName = (name: string, excludeIndex: number): boolean => {
        const trimmed = (name || "").trim().toLowerCase()
        if (!trimmed) return false
        const duplicate = data.some(
            (row, i) => i !== excludeIndex && (row.add_on_name || "").trim().toLowerCase() === trimmed
        )
        return duplicate
    }

    const updateAddOnName = (index: number, add_on_name: string) => {
        setDuplicateNameError(null)
        const updated = data.map((row, i) =>
            i === index ? { ...row, add_on_name } : row
        )
        onChange({ excursion_add_ons: updated })
        if (checkDuplicateName(add_on_name, index)) {
            setDuplicateNameError("Duplicate add-on names are not allowed.")
        }
    }

    const updateAddOnDefaultFlag = (index: number, flag_default: boolean) => {
        const updated = data.map((row, i) =>
            i === index ? { ...row, flag_default } : row
        )
        onChange({ excursion_add_ons: updated })
    }

    const updateAddOnPrice = (
        addOnIndex: number,
        categoryName: string,
        price: number
    ) => {
        const updated = [...data]
        const addOn = { ...updated[addOnIndex] }
        let pricing = ensurePricingForCategories(addOn, paidCategories, adultCategoryId)
        const idx = pricing.findIndex(
            (p) => p.category_name?.toLowerCase() === categoryName.toLowerCase()
        )
        if (idx >= 0) {
            pricing = pricing.map((p, i) =>
                i === idx ? { ...p, price } : p
            )
            addOn.add_on_pricing = pricing
            updated[addOnIndex] = addOn
            onChange({ excursion_add_ons: updated })
        }
    }

    const removeAddOn = (index: number) => {
        setDuplicateNameError(null)
        const updated = data.filter((_, i) => i !== index)
        onChange({ excursion_add_ons: updated })
    }

    const addRow = () => {
        setDuplicateNameError(null)
        const basePricing: ExcursionDetailAddOnPricingModel[] = [
            { add_on_pricing_id: "", excursion_add_on_id: "", category_id: adultCategoryId, category_name: ADULT_CATEGORY, price: 0 },
        ]
        for (const cat of paidCategories) {
            basePricing.push({
                add_on_pricing_id: "",
                excursion_add_on_id: "",
                category_id: cat.category_id,
                category_name: cat.category_name,
                price: 0,
            })
        }
        const newAddOn: ExcursionDetailAddOnModel = {
            excursion_add_on_id: "",
            excursion_id: excursion.excursion_id,
            add_on_name: "",
            flag_default: false,
            add_on_pricing: basePricing,
        }
        onChange({ excursion_add_ons: [...data, newAddOn] })
    }

    // const hasDuplicateNames = (): boolean => {
    //     const names = data.map((r) => (r.add_on_name || "").trim().toLowerCase()).filter(Boolean)
    //     return new Set(names).size !== names.length
    // }

    const priceColumnCount = 1 + paidCategories.length
    const priceColWidth = priceColumnCount > 0 ? `${Math.floor(100 / (priceColumnCount + 3))}%` : "20%"
    const nameColWidth = priceColumnCount > 0 ? `${Math.floor(100 / (priceColumnCount + 3))}%` : "22%"

    const columns: TableColumn<ExcursionDetailAddOnModel>[] = [
        {
            key: "add_on_name",
            label: "Add-On Name",
            align: "left",
            width: nameColWidth,
            // Use the Table's row index (not findIndex by id) — new rows share an empty
            // excursion_add_on_id, so findIndex collides on the first row and edits the wrong one.
            render: (value: string, _row: ExcursionDetailAddOnModel, index: number) => {
                return (
                    <>
                        <InputText
                            label=""
                            value={value ?? ""}
                            onChange={(e) => updateAddOnName(index, e.target.value)}
                            disabled={readOnly}
                        />
                        {checkDuplicateName(value ?? "", index) && (
                            <span style={{ color: "#d32f2f", fontSize: 12 }}>
                                Duplicate name
                            </span>
                        )}
                    </>
                )
            },
        },
        {
            key: "add_on_pricing",
            label: `${capitalize(ADULT_CATEGORY)} Price`,
            align: "left",
            width: priceColWidth,
            render: (_: unknown, row: ExcursionDetailAddOnModel, addOnIndex: number) => {
                const price = getPriceByCategory(row, ADULT_CATEGORY)
                const hasName = ((row.add_on_name ?? "").trim() ?? "") !== ""
                const isEmptyOrInvalid = hasName && (price == null || !Number.isFinite(price) || price <= 0)
                return (
                    <InputText
                        label=""
                        value={price === 0 ? "" : String(price)}
                        onChange={(e) =>
                            updateAddOnPrice(addOnIndex, ADULT_CATEGORY, Number(e.target.value) || 0)
                        }
                        disabled={readOnly}
                        error={error && isEmptyOrInvalid ? error : undefined}
                    />
                )
            },
        },
        ...paidCategories.map((cat) => ({
            key: `add_on_pricing_${(cat.category_name ?? "").toLowerCase()}`,
            label: `${capitalize(cat.category_name)} Price`,
            align: "left" as const,
            width: priceColWidth,
            render: (_: unknown, row: ExcursionDetailAddOnModel, addOnIndex: number) => {
                const price = getPriceByCategory(row, cat.category_name)
                const hasName = ((row.add_on_name ?? "").trim() ?? "") !== ""
                const isEmptyOrInvalid = hasName && (price == null || !Number.isFinite(price) || price <= 0)
                return (
                    <InputText
                        label=""
                        value={price === 0 ? "" : String(price)}
                        onChange={(e) =>
                            updateAddOnPrice(addOnIndex, cat.category_name, Number(e.target.value) || 0)
                        }
                        disabled={readOnly}
                        error={error && isEmptyOrInvalid ? error : undefined}
                    />
                )
            },
        })),
        {
            key: "flag_default",
            label: "Default",
            align: "center",
            width: "10%",
            render: (value: boolean, _row: ExcursionDetailAddOnModel, index: number) => {
                return (
                    <div style={{ display: "flex", justifyContent: "center" }}>
                        <Checkbox
                            label=""
                            checked={Boolean(value)}
                            onChange={(checked) => updateAddOnDefaultFlag(index, checked)}
                            disabled={readOnly}
                        />
                    </div>
                )
            },
        },
        {
            key: "excursion_add_on_id",
            label: "Action",
            align: "center",
            width: "8%",
            render: (_: unknown, _row: ExcursionDetailAddOnModel, index: number) => {
                return (
                    <button
                        type="button"
                        onClick={() => removeAddOn(index)}
                        disabled={readOnly}
                        style={{
                            background: "none",
                            border: "none",
                            cursor: readOnly ? "default" : "pointer",
                            padding: 0,
                            display: "inline-flex",
                            color: "#d32f2f",
                            alignItems: "center",
                        }}
                        aria-label="Remove add-on"
                    >
                        <IconsColor
                            iconName="deleteIcon"
                            width="24px"
                            height="24px"
                            fillColor="none"
                            strokeColor="currentColor"
                        />
                    </button>
                )
            },
        },
    ]

    return (
        <div className={utilStyles.pageContainerContent}>
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 12 }}>
                    <b>Add-Ons</b>
                </Grid>
                {(duplicateNameError || error) && (
                    <Grid size={{ xs: 12, md: 12 }}>
                        <span style={{ color: "#d32f2f", fontSize: 14 }}>
                            {duplicateNameError ?? error}
                        </span>
                    </Grid>
                )}
                <Grid size={{ xs: 12, md: 12 }}>
                    <Table
                        minWidth={150}
                        columns={columns}
                        data={data}
                        keyExtractor={(row, index) =>
                            row.excursion_add_on_id || `addon-${index}`
                        }
                    />
                </Grid>
                {!readOnly && (
                    <Grid size={{ xs: 12, md: 12 }}>
                        <button
                            type="button"
                            className={utilStyles.addBtn}
                            onClick={addRow}
                        >
                            + Add Row
                        </button>
                    </Grid>
                )}
            </Grid>
        </div>
    )
}

export default ExcursionAddOns
